/**
 * Custom hook for user management action handlers.
 * Extracted from UsersManagement to keep component files under 500 lines.
 *
 * @returns Action handlers, state setters, and loading states for user management
 */

import { useState, useCallback, useEffect } from 'react';
import { coreAPI, UserProfile, Role, UserManagementLog, Site, UserSitePermission } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { toast } from 'sonner';
import { TempPasswordResult } from './TempPasswordModal';

/** CR-5857: editable fields an admin may change via update_profile. */
export interface UpdateUserProfileFields {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  job_title?: string;
  department?: string;
  role?: number | null;
}

interface UseUserActionsReturn {
  // Data
  users: UserProfile[];
  availableRoles: Role[];
  activityLogs: UserManagementLog[];
  userSitePermissions: UserSitePermission[];
  // State
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  selectedUser: UserProfile | null;
  selectedRoleId: number | null;
  savingPermission: number | null;
  userDetailTab: 'profile' | 'companies';
  // Modal visibility
  showUserDetails: boolean;
  showChangeRoleDialog: boolean;
  showActivityLogDialog: boolean;
  showBulkApproveDialog: boolean;
  showBulkInviteDialog: boolean;
  showEditProfileDialog: boolean;
  /** Whether the "Set temp password" confirm dialog (with the force-change opt-in) is open. */
  showSetTempPasswordDialog: boolean;
  /** The user the "Set temp password" confirm dialog is targeting. */
  tempPasswordTarget: UserProfile | null;
  /** Result of the last "Set temp password" action, shown in its own modal. */
  tempPasswordResult: TempPasswordResult | null;
  // Setters
  setError: (error: string | null) => void;
  setSelectedUser: (user: UserProfile | null) => void;
  setSelectedRoleId: (id: number | null) => void;
  setUserDetailTab: (tab: 'profile' | 'companies') => void;
  setShowUserDetails: (show: boolean) => void;
  setShowChangeRoleDialog: (show: boolean) => void;
  setShowActivityLogDialog: (show: boolean) => void;
  setShowBulkApproveDialog: (show: boolean) => void;
  setShowBulkInviteDialog: (show: boolean) => void;
  setShowEditProfileDialog: (show: boolean) => void;
  setShowSetTempPasswordDialog: (show: boolean) => void;
  setTempPasswordResult: (result: TempPasswordResult | null) => void;
  // Actions
  fetchUsers: () => Promise<void>;
  handleToggleActive: (userProfile: UserProfile) => Promise<void>;
  handleSafeDelete: (userProfile: UserProfile) => Promise<void>;
  handleChangeRole: () => Promise<void>;
  handleToggleAdmin: (userProfile: UserProfile, isAdmin: boolean) => Promise<void>;
  handleToggleNotification: (userProfile: UserProfile, channel: 'in_app' | 'email', value: boolean) => Promise<void>;
  handleSendInvitation: (userProfile: UserProfile) => Promise<void>;
  handleAdminResetPassword: (userProfile: UserProfile) => Promise<void>;
  openSetTempPasswordDialog: (userProfile: UserProfile) => void;
  handleAdminSetTempPassword: (userProfile: UserProfile, forceChange: boolean) => Promise<void>;
  handleUpdateProfile: (
    userProfile: UserProfile,
    data: UpdateUserProfileFields
  ) => Promise<boolean>;
  openEditProfileDialog: (userProfile: UserProfile) => void;
  handleViewActivityLog: (userProfile: UserProfile) => Promise<void>;
  openChangeRoleDialog: (userProfile: UserProfile) => void;
  openUserDetails: (userProfile: UserProfile) => Promise<void>;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
  handleApproveUser: (userId: number) => Promise<void>;
  handleRejectUser: (userId: number) => Promise<void>;
  handleBulkApprove: (userIds: number[]) => Promise<void>;
  handleBulkSendInvitations: (profileIds: number[]) => Promise<void>;
  fetchAvailableRoles: () => Promise<void>;
}

/**
 * Hook encapsulating all user management API actions and related state.
 *
 * @returns Object with action handlers, data, and UI state for user management
 */
export function useUserActions(): UseUserActionsReturn {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [activityLogs, setActivityLogs] = useState<UserManagementLog[]>([]);
  const [userSitePermissions, setUserSitePermissions] = useState<UserSitePermission[]>([]);
  const [_companySites, setCompanySites] = useState<Site[]>([]);

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingPermission, setSavingPermission] = useState<number | null>(null);

  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [userDetailTab, setUserDetailTab] = useState<'profile' | 'companies'>('profile');

  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [showActivityLogDialog, setShowActivityLogDialog] = useState(false);
  const [showBulkApproveDialog, setShowBulkApproveDialog] = useState(false);
  const [showBulkInviteDialog, setShowBulkInviteDialog] = useState(false);
  const [showEditProfileDialog, setShowEditProfileDialog] = useState(false);
  const [showSetTempPasswordDialog, setShowSetTempPasswordDialog] = useState(false);
  const [tempPasswordTarget, setTempPasswordTarget] = useState<UserProfile | null>(null);
  const [tempPasswordResult, setTempPasswordResult] = useState<TempPasswordResult | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      // The API caps page_size at 100 (StandardResultsSetPagination), so a
      // single request silently drops every user past the first 100 — e.g. a
      // supplier admin whose scope spans hundreds of customer users would only
      // ever see the alphabetically-first 100, hiding newly-added users. Page
      // through until exhausted so the client-side filters see the full set.
      const PAGE = 100;
      const all: UserProfile[] = [];
      let page = 1;
      // Safety cap (50 pages = 5000 users) so a runaway never loops forever.
      while (page <= 50) {
        const response = await coreAPI.getUserProfiles({ page_size: PAGE, page });
        const batch = response.data.results || [];
        all.push(...batch);
        if (batch.length < PAGE || !response.data.next) break;
        page += 1;
      }
      setUsers(all);
    } catch (err: unknown) {
      console.error('Error fetching users:', err);
      setError(getApiErrorMessage(err, 'Failed to load users'));
    } finally { setLoading(false); }
  }, []);

  const fetchAvailableRoles = useCallback(async () => {
    try {
      const response = await coreAPI.getAvailableRoles();
      setAvailableRoles(response.data.roles || []);
    } catch (err: unknown) {
      console.error('Error fetching roles:', err);
    }
  }, []);

  useEffect(() => {
    void fetchAvailableRoles();
  }, [fetchAvailableRoles]);

  const handleToggleActive = useCallback(async (userProfile: UserProfile) => {
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.toggleUserActive(userProfile.id);
      const newIsActive = response.data.is_active;
      await fetchUsers();
      setSelectedUser((prev) =>
        prev?.id === userProfile.id ? { ...prev, user: { ...prev.user, is_active: newIsActive } } : prev
      );
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update user status'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  // CR-5312: backend decides delete-vs-deactivate based on the user's
  // login/audit footprint; we just surface the right toast.
  const handleSafeDelete = useCallback(async (userProfile: UserProfile) => {
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.safeDeleteUser(userProfile.id);
      const { deleted, deactivated, message } = response.data;
      if (deleted) toast.success(message || `User ${userProfile.user.email} deleted`);
      else if (deactivated) toast.success(message || `User ${userProfile.user.email} deactivated`);
      else toast.message(message || 'No change');
      await fetchUsers();
      // Close any open detail modal pointing at the deleted user.
      setSelectedUser((prev) => (prev?.id === userProfile.id && deleted ? null : prev));
      if (deleted) setShowUserDetails(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to delete user'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleChangeRoleImpl = async () => {
    if (!selectedUser || !selectedRoleId) return;
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.changeUserRole(selectedUser.id, selectedRoleId);
      await fetchUsers();
      setSelectedUser(response.data.user);
      setShowChangeRoleDialog(false); setSelectedRoleId(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to change user role'));
    } finally { setActionLoading(false); }
  };

  const handleToggleAdmin = useCallback(async (userProfile: UserProfile, isAdmin: boolean) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.toggleUserAdmin(userProfile.id, isAdmin);
      await fetchUsers();
      setSelectedUser((prev) =>
        prev?.id === userProfile.id
          ? { ...prev, user: { ...prev.user, is_superuser: isAdmin, is_staff: isAdmin } }
          : prev
      );
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update admin status'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleToggleNotification = useCallback(async (
    userProfile: UserProfile,
    channel: 'in_app' | 'email',
    value: boolean,
  ) => {
    try {
      setActionLoading(true); setError(null);
      const apiField = channel === 'in_app' ? 'in_app_enabled' : 'email_enabled';
      const profileField = channel === 'in_app' ? 'notif_in_app_enabled' : 'notif_email_enabled';
      await coreAPI.setUserNotificationSettings(userProfile.id, { [apiField]: value });
      // Optimistically reflect in the list + open modal without a full refetch.
      setUsers((prev) => prev.map((u) =>
        u.id === userProfile.id ? { ...u, [profileField]: value } : u));
      setSelectedUser((prev) =>
        prev?.id === userProfile.id ? { ...prev, [profileField]: value } : prev);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update notification settings'));
    } finally { setActionLoading(false); }
  }, []);

  const handleSendInvitation = useCallback(async (userProfile: UserProfile) => {
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.sendInvitationEmail(userProfile.id);
      toast.success(`Invitation email sent to ${userProfile.user.email}`);
      if (response.data.user) {
        const updated = response.data.user;
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
        setSelectedUser((prev) => (prev?.id === updated.id ? updated : prev));
      } else {
        await fetchUsers();
      }
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to send invitation email'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  // CR-5857: admin triggers a branded password-reset email for the user
  // without needing the user's inbox to start the flow.
  const handleAdminResetPassword = useCallback(async (userProfile: UserProfile) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.adminResetPassword(userProfile.id);
      toast.success(`Password reset email sent to ${userProfile.user.email}`);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to send password reset email'));
      toast.error(getApiErrorMessage(err, 'Failed to send password reset email'));
    } finally { setActionLoading(false); }
  }, []);

  // Open the confirm dialog where the admin chooses whether to force a change
  // on first login (default off) before generating the temp password.
  const openSetTempPasswordDialog = useCallback((userProfile: UserProfile) => {
    setTempPasswordTarget(userProfile);
    setShowSetTempPasswordDialog(true);
  }, []);

  // Set a temporary password directly — the reliable unblock when a user can't
  // use the email reset/invite link. forceChange (default off, chosen in the
  // confirm dialog) decides whether they must reset it on first login. The
  // password is surfaced in a dedicated modal (not a toast) so the admin can
  // reliably read/copy it to relay to the user.
  const handleAdminSetTempPassword = useCallback(async (userProfile: UserProfile, forceChange: boolean) => {
    try {
      setActionLoading(true); setError(null);
      const res = await coreAPI.adminSetTempPassword(userProfile.id, { force_change: forceChange });
      setShowSetTempPasswordDialog(false);
      setTempPasswordResult({
        email: userProfile.user.email,
        password: res.data.temp_password,
        forceChange: Boolean(res.data.force_change),
      });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to set temporary password'));
      toast.error(getApiErrorMessage(err, 'Failed to set temporary password'));
    } finally { setActionLoading(false); }
  }, []);

  // CR-5857: admin edits a managed user's identity / contact / role fields.
  // Returns true on success so the dialog can close itself.
  const handleUpdateProfile = useCallback(async (
    userProfile: UserProfile,
    data: UpdateUserProfileFields,
  ): Promise<boolean> => {
    try {
      setActionLoading(true); setError(null);
      const response = await coreAPI.updateUserProfileFields(userProfile.id, data);
      const updated = response.data.user;
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setSelectedUser((prev) => (prev?.id === updated.id ? updated : prev));
      toast.success('Profile updated');
      setShowEditProfileDialog(false);
      await fetchUsers();
      return true;
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Failed to update profile');
      setError(message);
      toast.error(message);
      return false;
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const openEditProfileDialog = useCallback((userProfile: UserProfile) => {
    setSelectedUser(userProfile);
    setShowEditProfileDialog(true);
  }, []);

  const handleViewActivityLog = useCallback(async (userProfile: UserProfile) => {
    try {
      setSelectedUser(userProfile); setActionLoading(true);
      const response = await coreAPI.getUserManagementLogs(userProfile.id);
      setActivityLogs(response.data || []);
      setShowActivityLogDialog(true);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to load activity logs'));
    } finally { setActionLoading(false); }
  }, []);

  const openChangeRoleDialog = useCallback((userProfile: UserProfile) => {
    setSelectedUser(userProfile);
    setSelectedRoleId(userProfile.role);
    setShowChangeRoleDialog(true);
  }, []);

  const openUserDetails = useCallback(async (userProfile: UserProfile) => {
    setSelectedUser(userProfile); setUserDetailTab('profile');
    setShowUserDetails(true); setUserSitePermissions([]); setCompanySites([]);
    // Only the per-user site permissions are needed here — that endpoint
    // already returns every group site with its permission + linkage. The old
    // parallel getSites() call was Helix-admin-only (403 for supplier admins
    // like Poeton's), and being in the same Promise.all it rejected the whole
    // load and left the Related Companies tab stuck on "Loading company
    // access...". Its result was unused (_companySites), so it's dropped.
    try {
      const permissionsResponse = await coreAPI.getUserSitePermissions(userProfile.id);
      setUserSitePermissions(permissionsResponse.data || []);
    } catch (err) {
      console.error('Error fetching site permissions:', err);
      setUserSitePermissions([]);
    }
  }, []);

  const handleToggleSiteLink = async (siteId: number, isLinked: boolean) => {
    if (!selectedUser) return;
    setSavingPermission(siteId);
    try {
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, {
        is_linked: isLinked, is_active: isLinked, can_purchasing: isLinked,
      });
      setUserSitePermissions(prev => prev.map(p => p.site === siteId ? response.data : p));
      await fetchUsers();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update site link'));
    } finally { setSavingPermission(null); }
  };

  const handleToggleSitePermission = async (siteId: number, permission: string, value: boolean) => {
    if (!selectedUser) return;
    setSavingPermission(siteId);
    try {
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, { [permission]: value });
      setUserSitePermissions(prev => prev.map(p => p.site === siteId ? response.data : p));
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to update permission'));
    } finally { setSavingPermission(null); }
  };

  const handleApproveUser = useCallback(async (userId: number) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.approveUser(userId);
      await fetchUsers();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to approve user'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleRejectUser = useCallback(async (userId: number) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.rejectUser(userId);
      toast.success('User rejected and deactivated');
      await fetchUsers();
      setSelectedUser(null);
      setShowUserDetails(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to reject user'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleBulkApprove = useCallback(async (userIds: number[]) => {
    try {
      setActionLoading(true); setError(null);
      await coreAPI.bulkApproveUsers(userIds);
      await fetchUsers();
      setShowBulkApproveDialog(false);
      toast.success('Selected pending users approved');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to approve users'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  const handleBulkSendInvitations = useCallback(async (profileIds: number[]) => {
    if (profileIds.length === 0) return;
    try {
      setActionLoading(true); setError(null);
      let ok = 0;
      let failed = 0;
      for (const id of profileIds) {
        try {
          const response = await coreAPI.sendInvitationEmail(id);
          ok += 1;
          if (response.data.user) {
            const updated = response.data.user;
            setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
          }
        } catch {
          failed += 1;
        }
      }
      await fetchUsers();
      if (failed === 0) {
        toast.success(`Invitation emails sent (${ok})`);
      } else if (ok === 0) {
        toast.error(`Failed to send ${failed} invitation(s)`);
      } else {
        toast.error(`Sent ${ok} invitation(s); ${failed} failed`);
      }
      setShowBulkInviteDialog(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Bulk invitation failed'));
    } finally { setActionLoading(false); }
  }, [fetchUsers]);

  return {
    users, availableRoles, activityLogs, userSitePermissions,
    loading, actionLoading, error, selectedUser, selectedRoleId,
    savingPermission, userDetailTab,
    showUserDetails, showChangeRoleDialog, showActivityLogDialog, showBulkApproveDialog,
    showBulkInviteDialog, showEditProfileDialog,
    showSetTempPasswordDialog, tempPasswordTarget, tempPasswordResult,
    setError, setSelectedUser, setSelectedRoleId, setUserDetailTab,
    setShowUserDetails, setShowChangeRoleDialog, setShowActivityLogDialog, setShowBulkApproveDialog,
    setShowBulkInviteDialog, setShowEditProfileDialog,
    setShowSetTempPasswordDialog, setTempPasswordResult,
    fetchUsers,
    handleToggleActive, handleSafeDelete, handleChangeRole: handleChangeRoleImpl, handleToggleAdmin,
    handleToggleNotification,
    handleSendInvitation, handleAdminResetPassword,
    openSetTempPasswordDialog, handleAdminSetTempPassword, handleUpdateProfile, openEditProfileDialog,
    handleViewActivityLog,
    openChangeRoleDialog, openUserDetails,
    handleToggleSiteLink, handleToggleSitePermission,
    handleApproveUser, handleRejectUser, handleBulkApprove, handleBulkSendInvitations,
    fetchAvailableRoles,
  };
}
